import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9oofj0senbniolesit0';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormState('success');
      form.reset();
      setTimeout(() => setFormState('idle'), 3000);
      return;
    }

    formData.delete('website_alt');

    setFormState('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse error
      }

      const code = (parsed as Record<string, unknown> | null)?.code;
      const metaMessage =
        ((parsed as Record<string, unknown> | null)?.meta as Record<string, unknown> | null)?.message ||
        (parsed as Record<string, unknown> | null)?.message;

      if (response.ok && code === 'OK') {
        setFormState('success');
        form.reset();
        setTimeout(() => setFormState('idle'), 4000);
      } else {
        setFormState('error');
        const msg = typeof metaMessage === 'string' ? metaMessage : '';
        if (msg && String(msg).toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后再试。');
        } else {
          setFormError(msg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="AboutPage"
        data={{
          name: '关于我们 - 歪歪漫画网',
          description: '歪歪漫画网品牌介绍、用户口碑评价与联系方式。歪歪漫画在线免费观看与阅读平台。',
          url: 'https://www.waiwaimanhua.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '歪歪漫画网',
            url: 'https://www.waiwaimanhua.com',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.waiwaimanhua.com' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: [
            {
              '@type': 'Question',
              name: '歪歪漫画网提供哪些漫画类型？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '歪歪漫画网提供韩漫、日漫、国漫等多种漫画类型，所有漫画均可在线免费观看和阅读，支持下拉式阅读模式。',
              },
            },
            {
              '@type': 'Question',
              name: '歪歪漫画网是否需要注册？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '歪歪漫画网无需注册即可免费观看所有漫画全集，随时随地畅享漫画阅读体验。',
              },
            },
            {
              '@type': 'Question',
              name: '什么是下拉式漫画阅读模式？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '下拉式漫画是一种流畅的阅读模式，用户只需向下滑动即可连续阅读整部漫画，无需翻页，带来丝滑的阅读体验。',
              },
            },
            {
              '@type': 'Question',
              name: '歪歪漫画网更新频率如何？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '歪歪漫画网每日更新最新的漫画章节，追更漫画第一时间上线，让用户不错过任何精彩内容。',
              },
            },
          ],
        }}
      />

      <Navbar />

      <main className="pt-20">
        <section className="bg-background-100 py-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-3">
              关于歪歪漫画网
            </h1>
          </div>
        </section>

        <section className="max-w-[1000px] mx-auto px-4 md:px-6 py-10">
          <div className="prose max-w-none">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
              品牌介绍
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              <strong>歪歪漫画网</strong>成立于2024年，是一个专注于<strong>歪歪漫画在线免费观看</strong>与<strong>歪歪漫画在线免费阅读</strong>的综合漫画平台。我们致力于为广大漫画爱好者提供一个<strong>免费漫画全集</strong>的在线阅读空间，囊括<strong>好看的韩漫</strong>、<strong>日漫在线</strong>阅读、<strong>国漫免费大全</strong>等丰富类别。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              歪歪漫画网创新性地推出了<strong>下拉式漫画</strong>阅读模式，用户无需翻页，只需轻松下拉即可连续阅读整部作品，带来前所未有的流畅阅读体验。无论你是在寻找<strong>追更漫画</strong>，还是想重温经典作品，歪歪漫画网都能满足你的需求。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              我们的团队由一群热爱漫画的年轻人组成，每天精心挑选和更新最优质的漫画内容，确保用户能在第一时间看到最新最全的漫画章节。歪歪漫画网始终坚持"免费、高清、快速"的服务理念，让每一位用户都能享受到最好的漫画阅读体验。
            </p>
          </div>
        </section>

        <section className="bg-background-100 py-10">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '漫画迷小王',
                  avatar: 'https://readdy.ai/api/search-image?query=Young%20Asian%20male%20portrait%20avatar%2C%20friendly%20smile%2C%20casual%20style%2C%20simple%20warm%20background%2C%20clean%20digital%20illustration%20style%2C%20headshot&width=120&height=120&seq=avatar-01&orientation=squarish',
                  comment: '歪歪漫画网真的太方便了！韩漫更新超快，下拉式阅读模式用起来特别舒服，已经推荐给身边所有爱看漫画的朋友了。',
                  rating: 5,
                },
                {
                  name: '日漫爱好者',
                  avatar: 'https://readdy.ai/api/search-image?query=Young%20Asian%20female%20portrait%20avatar%2C%20warm%20smile%2C%20modern%20casual%20look%2C%20simple%20soft%20background%2C%20clean%20digital%20illustration%20style%2C%20headshot&width=120&height=120&seq=avatar-02&orientation=squarish',
                  comment: '找日漫资源一直是我的痛点，直到发现了歪歪漫画网。画质清晰，更新及时，完全免费，简直是日漫爱好者的天堂！',
                  rating: 5,
                },
                {
                  name: '国漫铁粉',
                  avatar: 'https://readdy.ai/api/search-image?query=Young%20Asian%20male%20portrait%20avatar%2C%20cool%20expression%2C%20urban%20style%2C%20simple%20dark%20background%2C%20clean%20digital%20illustration%20style%2C%20headshot&width=120&height=120&seq=avatar-03&orientation=squarish',
                  comment: '国漫现在越来越好了，歪歪漫画网的国漫分类特别全，每天都有新作品上线。支持国产漫画，从歪歪漫画网开始！',
                  rating: 5,
                },
              ].map((review, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-lg p-5 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: review.rating }).map((_, j) => (
                          <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{review.comment}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1000px] mx-auto px-4 md:px-6 py-10">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-2">
            联系我们
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            如果您有任何问题、建议或合作意向，欢迎通过以下表单与我们联系。
          </p>

          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="max-w-[600px] space-y-4"
          >
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                您的姓名
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入您的姓名"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                电子邮箱
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入您的电子邮箱"
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                主题
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                required
                className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                placeholder="请输入主题"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                留言内容
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                required
                maxLength={500}
                className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                placeholder="请输入您的留言（最多500字）"
              ></textarea>
            </div>

            <input
              type="text"
              name="website_alt"
              className="sp-anti"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={formState === 'submitting'}
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
            >
              {formState === 'submitting' ? '提交中...' : '提交留言'}
            </button>

            {formState === 'success' && (
              <p className="text-sm text-green-600">留言已成功提交，感谢您的反馈！</p>
            )}
            {formState === 'error' && (
              <p className="text-sm text-red-500">{formError || '提交失败，请稍后再试。'}</p>
            )}
          </form>
        </section>

        <section className="bg-background-100 py-10">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
              常见问题
            </h2>
            <div className="space-y-3 max-w-[800px] mx-auto">
              {[
                {
                  q: '歪歪漫画网提供哪些漫画类型？',
                  a: '歪歪漫画网提供韩漫、日漫、国漫等多种漫画类型，所有漫画均可在线免费观看和阅读，支持下拉式阅读模式。无论是好看的韩漫、日漫在线还是国漫免费大全，歪歪漫画网都能满足你。',
                },
                {
                  q: '歪歪漫画网是否需要注册？',
                  a: '歪歪漫画网无需注册即可免费观看所有漫画全集，随时随地畅享漫画阅读体验。歪歪漫画在线免费观看，方便快捷。',
                },
                {
                  q: '什么是下拉式漫画阅读模式？',
                  a: '下拉式漫画是一种流畅的阅读模式，用户只需向下滑动即可连续阅读整部漫画，无需翻页，带来丝滑的阅读体验。歪歪漫画网的下拉式漫画功能让阅读更加轻松愉快。',
                },
                {
                  q: '歪歪漫画网更新频率如何？',
                  a: '歪歪漫画网每日更新最新的漫画章节，追更漫画第一时间上线，让用户不错过任何精彩内容。好看的韩漫、日漫在线、国漫免费大全均在持续更新中。',
                },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                >
                  <summary className="px-4 py-3 text-sm font-medium text-foreground-800 cursor-pointer hover:text-primary-600 transition-colors list-none flex items-center justify-between">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-4 pb-3 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}